<template>
  <div class="space-y-4">
    <div class="flex items-center justify-between">
      <h2>{{ t('apiKeys') }}</h2>
      <CVButton
        v-if="$can('create', 'ApiKey')"
        :is-loading="isCreating"
        @click="handleShowCreateModal"
      >
        {{ t('createApiKey') }}
      </CVButton>
    </div>

    <p class="text-sm text-gray-600">{{ t('description') }}</p>

    <div v-if="isLoading" class="flex justify-center py-4">
      <Spinner class="size-6" />
    </div>

    <div v-else-if="!apiKeysData || apiKeysData.length === 0" class="text-sm text-gray-500">
      {{ t('noApiKeys') }}
    </div>

    <div v-else class="space-y-3">
      <div v-for="apiKey in apiKeysData" :key="apiKey.id" :class="card()">
        <div class="space-y-3">
          <div class="flex items-start justify-between">
            <div class="flex-1">
              <div class="flex items-center gap-2">
                <h4 class="font-medium">{{ apiKey.name || t('unnamedApiKey') }}</h4>
                <span class="font-mono text-sm text-gray-500">{{ apiKey.keyPrefix }}****</span>
                <Badge v-if="!apiKey.user" variant="info">
                  {{ t('global') }}
                </Badge>
                <Badge v-else variant="neutral">
                  {{ apiKey.user.email }}
                </Badge>
              </div>
              <div class="mt-2 space-y-1 text-sm text-gray-600">
                <div>
                  {{ t('createdAt') }}: {{ formatDate(apiKey.createdDate, getDateFormat()) }}
                </div>
                <div v-if="apiKey.lastUsedAt">
                  {{ t('lastUsedAt') }}: {{ formatDate(apiKey.lastUsedAt, getDateFormat()) }}
                </div>
                <div v-else class="text-gray-400">{{ t('neverUsed') }}</div>
              </div>
            </div>
            <div class="flex gap-2">
              <CVButton
                v-if="$can('update', 'ApiKey') && !apiKey.user"
                variant="secondary"
                size="sm"
                @click="handleEditRules(apiKey)"
              >
                {{ t('editRules') }}
              </CVButton>
              <CVButton
                v-if="$can('delete', 'ApiKey')"
                variant="danger"
                size="sm"
                :is-loading="deletingId === apiKey.id"
                @click="handleDelete(apiKey)"
              >
                {{ t('delete') }}
              </CVButton>
            </div>
          </div>

          <div v-if="apiKey.rules && apiKey.rules.length > 0" class="rounded bg-gray-50 p-2">
            <div class="mb-1 text-xs font-medium text-gray-500">{{ t('rules') }}:</div>
            <div class="max-h-24 overflow-auto text-xs">
              <code class="whitespace-pre-wrap text-gray-700">{{
                JSON.stringify(apiKey.rules, null, 2)
              }}</code>
            </div>
          </div>
        </div>
      </div>
    </div>

    <Modal v-model="showCreateModal" :content-class="'max-w-2xl'" :close-on-click-outside="false">
      <template #title>
        <h3 class="text-lg font-semibold">{{ t('createGlobalApiKey') }}</h3>
      </template>
      <form class="space-y-4" @submit.prevent="handleCreate">
        <div>
          <label class="mb-2 block text-sm font-medium text-gray-700">{{ t('apiKeyName') }}</label>
          <Input v-model="newKeyName" :placeholder="t('apiKeyNamePlaceholder')" class="w-full" />
        </div>
        <div>
          <label class="mb-2 block text-sm font-medium text-gray-700">{{ t('rules') }}</label>
          <p class="mb-2 text-xs text-gray-500">{{ t('rulesDescription') }}</p>
          <JsonEditorVue
            v-model="newKeyRules"
            class="h-64"
            :mode="'text' as any"
            :main-menu-bar="false"
            :navigation-bar="false"
            :debounce="0"
          />
        </div>
        <div class="flex justify-end gap-2">
          <CVButton variant="secondary" type="button" @click="showCreateModal = false">
            {{ t('cancel') }}
          </CVButton>
          <CVButton
            :is-loading="isCreating"
            variant="default"
            :disabled="!newKeyName || newKeyName.trim() === '' || !isValidRules(newKeyRules)"
            type="submit"
          >
            {{ t('create') }}
          </CVButton>
        </div>
      </form>
    </Modal>

    <!-- Edit Rules Modal -->
    <Modal v-model="showEditModal" :content-class="'max-w-2xl'" :close-on-click-outside="false">
      <template #title>
        <h3 class="text-lg font-semibold">{{ t('editRulesFor') }} {{ editingApiKey?.name }}</h3>
      </template>
      <form class="space-y-4" @submit.prevent="handleSaveRules">
        <div>
          <label class="mb-2 block text-sm font-medium text-gray-700">{{ t('rules') }}</label>
          <p class="mb-2 text-xs text-gray-500">{{ t('rulesDescription') }}</p>
          <JsonEditorVue
            v-model="editingRules"
            class="h-80"
            :mode="'text' as any"
            :main-menu-bar="false"
            :navigation-bar="false"
            :debounce="0"
          />
        </div>
        <div class="flex justify-end gap-2">
          <CVButton variant="secondary" type="button" @click="showEditModal = false">
            {{ t('cancel') }}
          </CVButton>
          <CVButton
            :is-loading="isUpdating"
            variant="default"
            :disabled="!isValidRules(editingRules)"
            type="submit"
          >
            {{ t('save') }}
          </CVButton>
        </div>
      </form>
    </Modal>

    <!-- Show Plain Key Modal -->
    <Modal v-model="showPlainKeyModal" :content-class="'max-w-2xl'" :close-on-click-outside="true">
      <template #title>
        <h3 class="text-lg font-bold">{{ t('apiKeyCreated') }}</h3>
      </template>
      <div class="space-y-4">
        <div>
          <p class="mb-2 text-sm text-gray-700">{{ t('apiKeyWarning') }}</p>
          <div class="relative">
            <Input v-if="newlyCreatedKey" :value="newlyCreatedKey.plainKey" readonly />
            <button
              type="button"
              class="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-gray-500 hover:bg-gray-200 hover:text-gray-700"
              @click="copyToClipboard(newlyCreatedKey?.plainKey ?? '')"
            >
              <ClipboardDocumentIcon class="h-5 w-5" />
            </button>
          </div>
        </div>
        <div class="flex justify-end">
          <CVButton @click="showPlainKeyModal = false">{{ t('close') }}</CVButton>
        </div>
      </div>
    </Modal>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, defineAsyncComponent, useTemplateRef } from 'vue';
import { useI18n } from 'vue-i18n';
import CVButton from '@/components/buttons/CVButton.vue';
import Badge from '@/components/Badge.vue';
import Spinner from '@/components/icons/Spinner.vue';
import Modal from '@/components/Modal.vue';
import Input from '@/components/Input.vue';

// Lazy load the heavy JSON editor
const JsonEditorVue = defineAsyncComponent(() => import('json-editor-vue'));
import { formatDate } from '@/hooks/use-formatted-date.ts';
import { getDateFormat } from '@/hooks/use-date-format.ts';
import { Alert } from '@/services/alert.service';
import { card } from '@carvia/theme/lib/cva/card';
import { ClipboardDocumentIcon } from '@heroicons/vue/24/outline';
import {
  useGlobalApiKeys,
  useCreateGlobalApiKey,
  useUpdateApiKeyRules,
  useDeleteGlobalApiKey,
} from '@/queries/use-api-keys';
import type { ApiKey, ApiKeyRuleDto, CreateApiKeyResponseDto } from '@carvia/ros-client-types';
import { copyToClipboard } from '@/utils/clipboard';
import { cloneDeep } from 'lodash';

const { t } = useI18n();

// Queries
const { data: apiKeysData, isLoading } = useGlobalApiKeys();
const createMutation = useCreateGlobalApiKey();
const updateRulesMutation = useUpdateApiKeyRules();
const deleteMutation = useDeleteGlobalApiKey();

// State
const deletingId = ref<string | null>(null);
const showCreateModal = ref(false);
const showEditModal = ref(false);
const showPlainKeyModal = ref(false);
const newKeyName = ref('');
const newKeyRules = ref<ApiKeyRuleDto[]>([{ action: 'read', subject: 'Booking' }]);
const editingApiKey = ref<ApiKey | null>(null);
const editingRules = ref<ApiKeyRuleDto[]>([]);
const newlyCreatedKey = ref<CreateApiKeyResponseDto | null>(null);

const isCreating = computed(() => createMutation.isPending.value);
const isUpdating = computed(() => updateRulesMutation.isPending.value);

const parseRules = (rules: unknown): ApiKeyRuleDto[] | null => {
  // json-editor-vue in text mode returns a string, so we need to parse it
  if (typeof rules === 'string') {
    try {
      rules = JSON.parse(rules);
    } catch {
      return null;
    }
  }
  if (!Array.isArray(rules)) return null;
  if (rules.length === 0) return null;
  const isValid = rules.every(
    (rule) =>
      rule &&
      typeof rule === 'object' &&
      'action' in rule &&
      'subject' in rule &&
      (typeof rule.action === 'string' || Array.isArray(rule.action)) &&
      (typeof rule.subject === 'string' || Array.isArray(rule.subject)),
  );
  return isValid ? (rules as ApiKeyRuleDto[]) : null;
};

const isValidRules = (rules: unknown): boolean => {
  return parseRules(rules) !== null;
};

const handleShowCreateModal = () => {
  newKeyName.value = '';
  newKeyRules.value = [{ action: 'read', subject: 'Booking' }];
  showCreateModal.value = true;
};

const handleCreate = async () => {
  const rules = parseRules(newKeyRules.value);
  if (!newKeyName.value || newKeyName.value.trim() === '' || !rules) return;
  const result = await createMutation.mutateAsync({
    name: newKeyName.value.trim(),
    rules,
  });
  newlyCreatedKey.value = result;
  showCreateModal.value = false;
  showPlainKeyModal.value = true;
};

const handleEditRules = (apiKey: ApiKey) => {
  editingApiKey.value = apiKey;
  editingRules.value = apiKey.rules ? cloneDeep(apiKey.rules) : [];
  showEditModal.value = true;
};

const handleSaveRules = async () => {
  const rules = parseRules(editingRules.value);
  if (!editingApiKey.value || !rules) return;
  await updateRulesMutation.mutateAsync({
    id: editingApiKey.value.id,
    rules,
  });
  showEditModal.value = false;
  editingApiKey.value = null;
};

const handleDelete = async (apiKey: ApiKey) => {
  const result = await Alert.fire({
    titleText: t('deleteApiKey'),
    text: t('deleteApiKeyConfirm'),
    icon: 'warning',
    showDenyButton: true,
    showCancelButton: true,
    showConfirmButton: false,
    denyButtonText: t('delete'),
    cancelButtonText: t('cancel'),
  });

  if (!result.isDenied) return;

  deletingId.value = apiKey.id;
  await deleteMutation.mutateAsync(apiKey.id);
  deletingId.value = null;
};
</script>

<i18n lang="json">
{
  "en": {
    "apiKeys": "All API Keys",
    "description": "Manage API keys with custom CASL-based access control rules. These keys are not tied to any user and have fine-grained permissions defined by their rules.",
    "createApiKey": "Create API Key",
    "createGlobalApiKey": "Create Global API Key",
    "noApiKeys": "No global API keys found",
    "unnamedApiKey": "Unnamed API Key",
    "global": "Global",
    "createdAt": "Created",
    "lastUsedAt": "Last Used",
    "neverUsed": "Never used",
    "rules": "Rules",
    "editRules": "Edit Rules",
    "editRulesFor": "Edit Rules for",
    "delete": "Delete",
    "create": "Create",
    "save": "Save",
    "cancel": "Cancel",
    "close": "Close",
    "apiKeyName": "API Key Name",
    "apiKeyNamePlaceholder": "e.g. External Integration",
    "rulesDescription": "Define CASL rules as JSON. Each rule needs 'action' and 'subject'. Optional: 'fields', 'conditions', 'inverted', 'reason'.",
    "apiKeyCreated": "API Key Created",
    "apiKeyWarning": "This is your API key. Store it securely - you won't be able to see it again!",
    "deleteApiKey": "Delete API Key",
    "deleteApiKeyConfirm": "Are you sure you want to delete this API key? This action cannot be undone."
  },
  "de": {
    "apiKeys": "Alle API-Keys",
    "description": "Verwalten Sie API-Keys mit benutzerdefinierten CASL-basierten Zugriffsregeln. Diese Keys sind an keinen Benutzer gebunden und haben feingranulare Berechtigungen.",
    "createApiKey": "API-Schlüssel erstellen",
    "createGlobalApiKey": "API-Key erstellen",
    "noApiKeys": "Keine API-Keys gefunden",
    "unnamedApiKey": "Unbenannter API-Key",
    "global": "Global",
    "createdAt": "Erstellt",
    "lastUsedAt": "Zuletzt verwendet",
    "neverUsed": "Nie verwendet",
    "rules": "Regeln",
    "editRules": "Regeln bearbeiten",
    "editRulesFor": "Regeln bearbeiten für",
    "delete": "Löschen",
    "create": "Erstellen",
    "save": "Speichern",
    "cancel": "Abbrechen",
    "close": "Schließen",
    "apiKeyName": "API-Key Name",
    "apiKeyNamePlaceholder": "z.B. Externe Integration",
    "rulesDescription": "Definieren Sie CASL-Regeln als JSON. Jede Regel benötigt 'action' und 'subject'. Optional: 'fields', 'conditions', 'inverted', 'reason'.",
    "apiKeyCreated": "API-Key erstellt",
    "apiKeyWarning": "Dies ist Ihr API-Key. Speichern Sie ihn sicher - Sie werden ihn nicht mehr sehen können!",
    "deleteApiKey": "API-Key löschen",
    "deleteApiKeyConfirm": "Möchten Sie diesen API-Key wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden."
  },
  "es": {
    "apiKeys": "Todas las claves API",
    "description": "Gestione claves API con reglas de control de acceso basadas en CASL. No están vinculadas a ningún usuario y tienen permisos definidos por sus reglas.",
    "createApiKey": "Crear clave API",
    "createGlobalApiKey": "Crear clave API global",
    "noApiKeys": "No hay claves API globales",
    "unnamedApiKey": "Clave API sin nombre",
    "global": "Global",
    "createdAt": "Creada",
    "lastUsedAt": "Último uso",
    "neverUsed": "Nunca usada",
    "rules": "Reglas",
    "editRules": "Editar reglas",
    "editRulesFor": "Editar reglas de",
    "delete": "Eliminar",
    "create": "Crear",
    "save": "Guardar",
    "cancel": "Cancelar",
    "close": "Cerrar",
    "apiKeyName": "Nombre de la clave API",
    "apiKeyNamePlaceholder": "p. ej. Integración externa",
    "rulesDescription": "Defina reglas CASL en JSON. Cada regla necesita «action» y «subject». Opcional: «fields», «conditions», «inverted», «reason».",
    "apiKeyCreated": "Clave API creada",
    "apiKeyWarning": "Esta es su clave API. Guárdela de forma segura: no podrá volver a verla.",
    "deleteApiKey": "Eliminar clave API",
    "deleteApiKeyConfirm": "¿Seguro que desea eliminar esta clave API? Esta acción no se puede deshacer."
  }
}
</i18n>
